import { Fragment, useContext, useEffect, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { FilePlus, LocateFixed, Pin } from "lucide-react"

// ** UI Components
import { Badge } from "@components/common/badge"
import { Button } from "@components/common/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "../common/tooltip"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@components/common/card"

// ** Routes
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Routes
import {
  GET_CONTRACTOR,
  GET_DICTIONARY_RESOURCES_ENDPOINT,
  GET_STATUS,
  GET_WEATHER,
  SERVER_URL,
  WORK_DIARY_GET_BY_ID,
} from "@pma/utils/api-routes"

// ** Custom Context
import { ProjectContext } from "@pma/contexts/ProjectContext"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"

// ** Custom Components
import PinProject from "./pin-project"
import DateEditor from "./date-editor"
import ProjectEditor from "./project-editor"
import DeleteProject from "./delete-project"
import StatusEditor from "./project-status-editor"
import { NewWorkDiaries } from "./grouped-tasks/New-work-diaries"

const ProjectCard = ({ project }: any) => {
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const { translate } = useTranslator()

  const orgId = authConfig?.last_organization_id
  const { isEditing } = useContext(ProjectContext)
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const [diaryId, setDiaryId] = useState<number>(0)
  const [workDetails, setWorkDetails] = useState(undefined)
  const [createWorkDiaryModal, setCreateWorkDiaryModal] = useState<boolean>(false)

  const goToProject = (id: number) => navigate(`${PROJECTS_PAGE_ROUTE}/${id}`)

  const handleCardClick = (event: React.MouseEvent) => {
    if (isEditing) return
    const target = event.target as HTMLElement
    if (target.closest(".edit-icon")) return
    if (target.closest(".delete-icon")) return
    // Prevent navigation for interactive elements
    if (!target.classList.contains("edit-icon")) {
      goToProject(project.id)
    }
  }

  const getStatusByOrg = async () => {
    const response = await axios.get(SERVER_URL + GET_STATUS, {
      params: { organization_id: orgId },
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const getWeatherByOrg = async () => {
    const response = await axios.get(SERVER_URL + GET_WEATHER, {
      params: { organization_id: orgId },
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const getResourcesStructuresOrg = async (key: string, value: boolean) => {
    const response = await axios.get(SERVER_URL + GET_DICTIONARY_RESOURCES_ENDPOINT, {
      params: { organization_id: orgId, [key]: value },
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const statusResult = useQuery({
    queryKey: ["status-context"],
    queryFn: () => getStatusByOrg(),
    staleTime: 1000 * 60 * 15, // 15 minutes for status data
    refetchOnWindowFocus: false,
  })

  const weatherResult = useQuery({
    queryKey: ["weather-context"],
    queryFn: () => getWeatherByOrg(),
    staleTime: 1000 * 60 * 30, // 30 minutes for weather data
    refetchOnWindowFocus: false,
  })

  const resourcesStructuresResult = useQuery({
    queryKey: ["resources-structures-context"],
    queryFn: () => getResourcesStructuresOrg("get_structures", true),
    staleTime: 1000 * 60 * 20, // 20 minutes
    refetchOnWindowFocus: false,
  })

  const resourcesWorkforceResult = useQuery({
    queryKey: ["resources-workforce-context"],
    queryFn: () => getResourcesStructuresOrg("get_employees", true),
    staleTime: 1000 * 60 * 20, // 20 minutes
    refetchOnWindowFocus: false,
  })

  const resourcesToolsResult = useQuery({
    queryKey: ["resources-tools-context"],
    queryFn: () => getResourcesStructuresOrg("get_tools", true),
    staleTime: 1000 * 60 * 20, // 20 minutes
    refetchOnWindowFocus: false,
  })

  const getContractorByProject = async () => {
    const response = await axios.get(SERVER_URL + GET_CONTRACTOR, {
      params: { project_id: project.id },
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const contractorResult = useQuery({
    queryKey: ["contractor-context"],
    queryFn: () => getContractorByProject(),
    staleTime: 1000 * 60 * 15, // 15 minutes
    refetchOnWindowFocus: false,
  })

  const getWorkDiaryDetailsById = async (diaryId: number) => {
    const response = await axios.get(SERVER_URL + WORK_DIARY_GET_BY_ID + diaryId, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return response.data
  }

  const { data: workDiaryDetail, isFetching: workDiaryDetailIsFetching } = useQuery({
    queryKey: ["get-work-diary-details", diaryId],
    queryFn: () => getWorkDiaryDetailsById(diaryId),
    enabled: !!diaryId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (!workDiaryDetailIsFetching && workDiaryDetail) setWorkDetails(workDiaryDetail)
  }, [workDiaryDetailIsFetching, workDiaryDetail])

  return (
    <Fragment>
      <Card onClick={handleCardClick} className='cursor-pointer hover:shadow-lg'>
        <CardHeader>
          <CardTitle>
            <ProjectEditor
              id={project?.id}
              title={"title"}
              value={project?.title}
              root_folder={project?.root_folder}
            >
              {project?.title}
            </ProjectEditor>
          </CardTitle>
        </CardHeader>
        <CardContent className='py-0'>
          <div className='flex flex-col'>
            <ProjectEditor id={project?.id} title={"description"} value={project?.description}>
              <p className='mb-2 min-h-6 max-w-[30ch] truncate text-ellipsis text-sm text-slate-500'>
                {project?.description || "---"}
              </p>
            </ProjectEditor>

            {project?.status && <StatusEditor projectId={project?.id} status={project?.status} />}
            <div className='mt-4 flex w-full justify-between'>
              <div className='flex flex-1 flex-col gap-1'>
                <strong className='text-xs text-slate-500'>{translate("Type")}</strong>
                <div className='flex items-center' onClick={(e) => e.stopPropagation()}>
                  <Badge
                    variant={project?.type_id === 2 ? "premium" : "secondary"}
                    className='h-6 min-w-[70px] capitalize'
                  >
                    {project?.type_id === 2 ? translate("Base") : translate("Water")}
                  </Badge>
                </div>
              </div>

              <div
                className='flex w-fit flex-col items-center text-sm'
                onClick={(e) => e.stopPropagation()}
              >
                <strong className='w-full text-left text-xs text-slate-500'>
                  {translate("Duration")}
                </strong>
                <DateEditor
                  value={{
                    from: project?.start_at,
                    to: project?.finish_to,
                  }}
                  projectId={project?.id}
                />
              </div>
            </div>
          </div>

          <div className='flex flex-1 flex-col gap-1'>
            <ProjectEditor
              id={project?.id}
              title={"address_text"}
              type='address'
              value={project?.address_text}
            >
              <div className='flex items-center'>
                <LocateFixed size={16} className='mx-2' />
                <small className='my-3 flex items-center gap-2 text-wrap text-xs text-slate-500'>
                  {project?.address_text || "---"}
                </small>
              </div>
            </ProjectEditor>
          </div>
        </CardContent>
        <CardFooter className='mb-2 flex justify-end py-0'>
          <Tooltip>
            <TooltipTrigger>
              <FilePlus
                className='mx-2'
                size={14}
                onClick={(e) => {
                  e.stopPropagation()
                  setCreateWorkDiaryModal(!createWorkDiaryModal)
                }}
              />
            </TooltipTrigger>
            <TooltipContent alignOffset={20}>{translate("Create Work Diary")}</TooltipContent>
          </Tooltip>

          <PinProject project={project}>
            <Pin size={14} />
          </PinProject>

          <Button onClick={() => goToProject(project.id)} variant={"link"} size={"sm"}>
            {translate("View")}
          </Button>
          <div onClick={(e) => e.stopPropagation()}>
            <DeleteProject project={project} className='delete-icon' />
          </div>
        </CardFooter>
      </Card>

      {createWorkDiaryModal && (
        <NewWorkDiaries
          taskModal={createWorkDiaryModal}
          setTaskModal={setCreateWorkDiaryModal}
          lstStatus={statusResult?.data || []}
          lstWeather={weatherResult?.data || []}
          lstStructures={resourcesStructuresResult.data || []}
          lstWorkforce={resourcesWorkforceResult.data || []}
          lstTools={resourcesToolsResult.data || []}
          lstContractor={contractorResult?.data || []}
          project={project}
          workDiaries={workDetails}
          setDiaryId={setDiaryId}
          refetchWorkDiary={() => {}}
        />
      )}
    </Fragment>
  )
}

export default ProjectCard
